/**
 * The `sorted` prop for a Semantic UI `Table.HeaderCell`, as a spreadable object.
 *
 * This app's sort state comes from `utils/SortReducer`, which seeds `direction` as
 * `null` and returns `'ascending' | 'descending'` thereafter; `SortReducer.spec.ts`
 * pins that `null` seed. Every sorted header renders
 * `sorted={column === 'x' ? direction : null}`, so `null` reaches the prop both from
 * the seed and from the non-matching arm of all 79 of those ternaries.
 *
 * Semantic declares `sorted?: 'ascending' | 'descending'`. Under
 * `exactOptionalPropertyTypes` that admits the property being ABSENT but rejects it
 * being present and holding `undefined` — so neither `sorted={null}` nor
 * `sorted={undefined}` type-checks, and returning `undefined` from a helper does not
 * help. Omitting the property is the only spelling the declaration accepts.
 *
 * Spreading `{}` omits it. That is what the runtime already did with `null`:
 * `TableHeaderCell` renders the prop through `useValueAndKey(sorted, 'sorted')`,
 * which is `val && val !== true && val + ' ' + key`, so `null`, `undefined` and an
 * absent prop all short-circuit at `val &&` and contribute nothing to the className.
 * Verified against the installed semantic-ui-react 2.1.5 and its clsx: all three
 * produce `className === ''`.
 *
 * React itself treats an absent prop and an `undefined` prop identically for a
 * component that reads it out of `props`, so this changes the props object's key set
 * without changing any rendered output.
 */
export type SortDirection = 'ascending' | 'descending';

export const sortDirection = (direction: SortDirection | null | undefined): { sorted?: SortDirection } =>
  direction ? { sorted: direction } : {};
